import * as tslib_1 from "tslib";
import { HttpClient } from '@angular/common/http';
import { forkJoin, pipe, EMPTY } from 'rxjs';
import { map, expand, startWith, takeWhile, reduce } from 'rxjs/operators';
import { Page } from '@app/models/page';
import { Query } from '@app/models/queries/queries.model';
import { config } from '@config/config';
import { AuthState } from '@app/auth/auth.state';
import { EnvelopeUtils } from '@app/utils/envelope.utils';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../auth/auth.state";
var Fetch = /** @class */ (function () {
    function Fetch(http, authState) {
        this.http = http;
        this.authState = authState;
    }
    /**
     * Makes a single request and returns the API envelope
     */
    Fetch.prototype.get = function (url) {
        var fullUrl = "" + this.constructUrl(url);
        var req = this.http.get(fullUrl);
        return {
            url: fullUrl,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: function (modelClass) {
                return req.pipe(map(function (res) { return res.data.map(function (m) { return new modelClass(m); }); }), EnvelopeUtils.handleError());
            },
            model: function (modelClass) {
                return req.pipe(map(function (res) { return new modelClass(res.data[0]); }), EnvelopeUtils.handleError());
            },
        };
    };
    /**
     * Makes a single request and returns the API envelope
     */
    Fetch.prototype.getOne = function (url, id) {
        var fullUrl = this.constructUrl(url) + "/" + id;
        var req = this.http.get(fullUrl);
        return {
            url: fullUrl,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: function (modelClass) {
                return req.pipe(map(function (res) { return res.data.map(function (m) { return new modelClass(m); }); }), EnvelopeUtils.handleError());
            },
            model: function (modelClass) {
                return req.pipe(map(function (res) { return new modelClass(res.data[0]); }), EnvelopeUtils.handleError());
            },
        };
    };
    /**
     * Makes multiple individual requests and strings together the responses
     * by taking the first API envelope, appending the data from every
     * response after, then returning that initial envelope with all the data.
     */
    Fetch.prototype.getSome = function (url, ids) {
        var _this = this;
        var e_1, _a;
        var fullUrls = ids.map(function (id) { return _this.constructUrl(url) + "/" + id; });
        var requests = [];
        try {
            for (var fullUrls_1 = tslib_1.__values(fullUrls), fullUrls_1_1 = fullUrls_1.next(); !fullUrls_1_1.done; fullUrls_1_1 = fullUrls_1.next()) {
                var u = fullUrls_1_1.value;
                requests.push(this.http.get(u));
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (fullUrls_1_1 && !fullUrls_1_1.done && (_a = fullUrls_1.return)) _a.call(fullUrls_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        var req = forkJoin(requests).pipe(map(function (envelopes) {
            return envelopes.reduce(function (acc, val) {
                acc.data = acc.data.concat(val.data);
                return acc;
            }, envelopes[0]);
        }));
        return {
            url: fullUrls,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: function (modelClass) {
                return req.pipe(map(function (res) { return res.data.map(function (m) { return new modelClass(m); }); }), EnvelopeUtils.handleError());
            },
            model: function (modelClass) {
                return req.pipe(map(function (res) { return new modelClass(res.data[0]); }), EnvelopeUtils.handleError());
            },
        };
    };
    /**
     * Fetches a collection from the API. Limit and offset are calculated from params.
     * @param page The page number to retrieve
     * @param options Any options that are passed as query params
     * but if this param is specified, it would be /utilities/:id/:path
     */
    Fetch.prototype.getPage = function (url, page, options) {
        var fullUrl = this.constructUrl(url);
        var params = tslib_1.__assign({ limit: page.limit, offset: page.offset }, options);
        if (page.sortField && page.sortDirection) {
            params.sort_field = page.sortField;
            params.sort_direction = page.sortDirection;
        }
        var req = this.http.get(fullUrl, { params: params });
        return {
            url: fullUrl,
            params: params,
            page: page,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: function (modelClass) {
                return req.pipe(map(function (res) {
                    return res.data.map(function (m) {
                        var mod = new modelClass(m);
                        mod.setTotalCount(res.count);
                        return mod;
                    });
                }), EnvelopeUtils.handleError());
            },
            model: function (modelClass) {
                return req.pipe(map(function (res) {
                    var mod = new modelClass(res.data[0]);
                    mod.setTotalCount(res.count);
                    return mod;
                }), EnvelopeUtils.handleError());
            },
        };
    };
    /**
     * Fetches all models from the API. Recursively fetches pages of the
     * maximum size and appends them together into one envelope.
     * @param options Any options that are passed as query params
     */
    Fetch.prototype.getAll = function (url, options) {
        var _this = this;
        var fullUrl = this.constructUrl(url);
        var page = new Page(1, config.maxPageSize);
        var req = this.getPage('/' + fullUrl, page, options).req.pipe(expand(function (data) {
            if (data.pagination && data.pagination.next) {
                var nextPage = Page.fromUrl(data.pagination.next);
                return _this.getPage('/' + fullUrl, nextPage, options).req;
            }
            return EMPTY.pipe(startWith(null)); // tslint:disable-line TODO look into the deprecation and update
        }), takeWhile(function (val) { return val !== null; }), reduce(function (acc, curr) {
            acc.data = acc.data.concat(curr.data);
            return acc;
        }));
        return {
            url: fullUrl,
            params: tslib_1.__assign({ all: true }, options),
            req: req.pipe(EnvelopeUtils.handleError()),
            models: function (modelClass) {
                return req.pipe(map(function (res) {
                    return res.data.map(function (m) {
                        var mod = new modelClass(m);
                        mod.setTotalCount(res.count);
                        return mod;
                    });
                }), EnvelopeUtils.handleError());
            },
            model: function (modelClass) {
                return req.pipe(map(function (res) {
                    var mod = new modelClass(res.data[0]);
                    mod.setTotalCount(res.count);
                    return mod;
                }), EnvelopeUtils.handleError());
            },
        };
    };
    Fetch.prototype.post = function (url, body) {
        var fullUrl = this.constructUrl(url);
        return this.http.post(fullUrl, body).pipe(map(function (res) {
            res.message = EnvelopeUtils.message(res);
            return res;
        }), EnvelopeUtils.handleError());
    };
    Fetch.prototype.patch = function (url, id, body) {
        var fullUrl = this.constructUrl(url) + "/" + id;
        return this.http.patch(fullUrl, body).pipe(map(function (res) {
            res.message = EnvelopeUtils.message(res);
            return res;
        }), EnvelopeUtils.handleError());
    };
    Fetch.prototype.delete = function (url, id) {
        var fullUrl = this.constructUrl(url) + "/" + id;
        return this.http.delete(fullUrl).pipe(map(function (res) {
            res.message = EnvelopeUtils.message(res);
            return res;
        }), EnvelopeUtils.handleError());
    };
    /**
     * Fetches one or more queries, combining them efficiently to reduce network requests.
     */
    Fetch.prototype.query = function (queries) {
        var _this = this;
        var batchedByDevice = {};
        // Group queries by device id but different query id
        for (var i = 0; i < queries.length; i++) {
            if (!(queries[i] instanceof Query)) {
                queries[i] = new Query(queries[i]);
            }
            var query = queries[i];
            if (batchedByDevice[query.device_id]) {
                batchedByDevice[query.device_id].push(query);
            }
            else {
                batchedByDevice[query.device_id] = [query];
            }
        }
        var requests = [];
        requests = Object.values(batchedByDevice).reduce(function (acc, batch) {
            var httpReqs = [];
            for (var i = 0; i < batch.length; i += config.maxQueries) {
                httpReqs.push(_this.http.post(_this.constructUrl("/devices/" + batch[0].device_id + "/query"), {
                    queries: batch.slice(i, i + config.maxQueries).map(function (query) { return query.toRequestBody(); }),
                }));
            }
            return acc.concat(httpReqs);
        }, []);
        var req = forkJoin(requests).pipe(this.setDataPipe(queries));
        return {
            url: '',
            req: req.pipe(EnvelopeUtils.handleError()),
            models: function (modelClass) {
                return req.pipe(map(function (res) { return res.data; }), EnvelopeUtils.handleError());
            },
            model: function (modelClass) {
                return req.pipe(map(function (res) { return res.data[0]; }), EnvelopeUtils.handleError());
            },
        };
    };
    Fetch.prototype.setDataPipe = function (queries) {
        return pipe(map(function (entries) {
            var e_2, _a;
            var combinedData = entries.reduce(function (a, entry) { return Object.assign(a, entry.data[0]); }, {});
            var _loop_1 = function (key) {
                var oldQuery = queries.find(function (query) { return query.id === key; });
                oldQuery.setData(combinedData[key]);
            };
            try {
                for (var _b = tslib_1.__values(Object.keys(combinedData)), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var key = _c.value;
                    _loop_1(key);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_2) throw e_2.error; }
            }
            return {
                success: true,
                code: 0,
                message: '',
                http_code: 200,
                http_message: '',
                data: queries,
                count: 0,
            };
        }));
    };
    // If url begins with //, don't prepend the base url
    Fetch.prototype.constructUrl = function (url) {
        if (url.slice(0, 2) === '//') {
            return url.slice(1);
        }
        return "" + this.authState.baseURL() + url;
    };
    Fetch.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function Fetch_Factory() { return new Fetch(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.AuthState)); }, token: Fetch, providedIn: "root" });
    return Fetch;
}());
export { Fetch };
